<template>
  <div class="payroll-projects">
    <a-table
      row-key="key"
      :data-source="rows"
      :pagination="false"
      size="middle">
      <a-table-column
        title="Проект"
        data-index="name" />

      <a-table-column
        title="Планировок"
        data-index="plans_count"
        :width="130" />

      <a-table-column
        title="BRP"
        :width="160">
        <template #default="{ record }">
          <span v-if="record.brpHint">{{ record.brpHint }}</span>
          <span v-else>—</span>
        </template>
      </a-table-column>

      <a-table-column
        title="Начислено"
        :width="150">
        <template #default="{ record }">
          <b>{{ formatAmount(record.amount) }}</b>
        </template>
      </a-table-column>

      <template #expandedRowRender="{ record }">
        <a-table
          row-key="key"
          :data-source="record.plans"
          :pagination="false"
          size="small">
          <a-table-column
            title="Планировка"
            data-index="crm_plan_name" />

          <a-table-column
            title="Тип работы"
            :width="180">
            <template #default="{ record: plan }">
              {{ workTypeLabel(plan) }}
            </template>
          </a-table-column>

          <a-table-column
            title="BRP"
            :width="110">
            <template #default="{ record: plan }">
              {{ plan.brp === null ? '—' : formatAmount(plan.brp) }}
            </template>
          </a-table-column>

          <a-table-column
            title="Начислено"
            :width="200">
            <template #default="{ record: plan }">
              <b>{{ formatAmount(plan.amount) }}</b>
              <a-tooltip
                v-if="plan.is_overridden"
                :title="overrideTooltip(plan)">
                <a-tag
                  color="orange"
                  style="margin-left: 8px">
                  правка
                </a-tag>
              </a-tooltip>
            </template>
          </a-table-column>

          <a-table-column
            v-if="editable"
            title=""
            :width="190">
            <template #default="{ record: plan }">
              <a-button
                type="link"
                size="small"
                @click="openEdit(plan)">
                Изменить
              </a-button>
              <a-button
                v-if="plan.is_overridden"
                type="link"
                size="small"
                :loading="resetting === planKey(plan)"
                @click="resetAmount(plan)">
                Сбросить
              </a-button>
            </template>
          </a-table-column>
        </a-table>
      </template>

      <template #emptyText>
        За выбранный расчётный период завершённых работ нет
      </template>
    </a-table>

    <a-modal
      v-model:open="edit.open"
      title="Ручная корректировка начисления"
      :confirm-loading="edit.saving"
      ok-text="Сохранить"
      cancel-text="Отмена"
      @ok="saveAmount">
      <a-form layout="vertical">
        <a-form-item label="Планировка">
          {{ edit.plan?.crm_plan_name }} — {{ workTypeLabel(edit.plan) }}
        </a-form-item>
        <a-form-item label="Расчётное значение">
          {{ formatAmount(edit.plan?.calculated_amount ?? 0) }} BRP
        </a-form-item>
        <a-form-item
          label="Начисление, BRP"
          :validate-status="edit.error ? 'error' : ''"
          :help="edit.error">
          <a-input-number
            v-model:value="edit.amount"
            :min="0"
            :step="0.5"
            style="width: 100%" />
        </a-form-item>
        <a-form-item label="Комментарий">
          <a-textarea
            v-model:value="edit.comment"
            :rows="3"
            :maxlength="1000"
            placeholder="Причина корректировки" />
        </a-form-item>
      </a-form>

      <a-collapse v-if="edit.log.length">
        <a-collapse-panel
          key="log"
          header="История правок">
          <a-timeline>
            <a-timeline-item
              v-for="entry in edit.log"
              :key="entry.id">
              <div>
                {{ formatLogAmount(entry.old_amount) }} → {{ formatLogAmount(entry.new_amount) }}
              </div>
              <div class="payroll-projects__log-meta">
                {{ entry.author?.name ?? '—' }}, {{ formatDate(entry.created_at) }}
              </div>
              <div v-if="entry.comment">
                {{ entry.comment }}
              </div>
            </a-timeline-item>
          </a-timeline>
        </a-collapse-panel>
      </a-collapse>
    </a-modal>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import moment from 'moment';
import { api } from '@/api';
import { formatAmount, planKey, workTypeLabel } from '@/Components/CRM/Payroll/payrollFormat';

const props = defineProps({
  projects: {
    type: Array,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['changed']);

const resetting = ref(null);

const edit = reactive({
  open: false,
  saving: false,
  plan: null,
  amount: 0,
  comment: '',
  error: '',
  log: [],
});

const formatLogAmount = (value) => (value === null ? 'расчётное' : `${formatAmount(value)} BRP`);
const formatDate = (value) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '—');

const rows = computed(() => props.projects.map((project) => {
  const workTypes = new Set(project.plans.map((plan) => plan.work_type));
  const brp = workTypes.size === 1 ? project.brp[[...workTypes][0]] : null;

  return {
    ...project,
    key: project.apartment?.id ?? 'no-apartment',
    name: project.apartment?.name ?? 'Без апартамента',
    brpHint: brp === null || brp === undefined ? null : `${formatAmount(brp)} BRP`,
    plans: project.plans.map((plan) => ({ ...plan, key: planKey(plan) })),
  };
}));

const overrideTooltip = (plan) => {
  const base = `Расчётное значение: ${formatAmount(plan.calculated_amount)} BRP`;
  return plan.override_comment ? `${base}. ${plan.override_comment}` : base;
};

const openEdit = async (plan) => {
  edit.plan = plan;
  edit.amount = plan.amount;
  edit.comment = plan.override_comment ?? '';
  edit.error = '';
  edit.log = [];
  edit.open = true;

  try {
    const { data } = await api.getPayrollAmountLog(plan.crm_plan_id);
    edit.log = data.items.filter((entry) => entry.work_type === plan.work_type && entry.type === plan.type);
  } catch {
    edit.log = [];
  }
};

const saveAmount = async () => {
  if (edit.amount === null || edit.amount === undefined || edit.amount < 0) {
    edit.error = 'Укажите начисление';
    return;
  }

  edit.error = '';
  edit.saving = true;

  try {
    await api.setPayrollAmount(edit.plan.crm_plan_id, {
      type: edit.plan.type,
      workType: edit.plan.work_type,
      amount: edit.amount,
      comment: edit.comment || null,
    });

    edit.open = false;
    message.success('Начисление изменено');
    emit('changed');
  } catch (e) {
    message.error(e.response?.data?.message ?? 'Не удалось изменить начисление');
  } finally {
    edit.saving = false;
  }
};

const resetAmount = async (plan) => {
  resetting.value = planKey(plan);

  try {
    await api.resetPayrollAmount(plan.crm_plan_id, { type: plan.type, workType: plan.work_type });
    message.success('Начисление возвращено к расчётному');
    emit('changed');
  } catch (e) {
    message.error(e.response?.data?.message ?? 'Не удалось сбросить начисление');
  } finally {
    resetting.value = null;
  }
};
</script>

<style lang="scss" scoped>
.payroll-projects {
  &__log-meta {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
}
</style>
